import axios from "axios";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_FILE_URL, BASE_URL, multiSelectStyle } from "../../../constant";
import { ToastContainer, toast } from "react-toastify";
import { ComboBox, MultiSelect } from "@progress/kendo-react-dropdowns";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import AppContext from "../../../AppContext";
import { ReactSVG } from "react-svg";

const TeamForm = () => {
  const defaultTeam = {
    name: "",
    description: "",
    active: true,
    assets: [],
  };
  const { t } = useTranslation();
  const { id } = useParams();
  const { authCtx, darkMode } = useContext(AppContext);
  const navigate = useNavigate();
  const [team, setTeam] = useState(defaultTeam);
  const [loading, setLoading] = useState(false);
  const [assetTypes, setAssetTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  const loadAssets = async (assetTypeIds = []) => {
    setAssetsLoading(true);
    const companyId = authCtx.profile?.companyId;

    try {
      const promises = assetTypeIds.map(async (assetTypeId) => {
        const res = await axios.get(`${BASE_URL}/api/Asset`, {
          params: { assetTypeId, companyId },
        });
        return res.data.map((asset) => ({
          label: asset.name,
          value: asset.id,
          assetType: asset.assetType,
        }));
      });

      const assetData = await Promise.all(promises);

      // Flatten the array of arrays into a single array
      const flattenedData = assetData.flat();

      if (flattenedData.length > 0) {
        setAssets([{ value: "~all", label: "ALL" }, ...flattenedData]);
      } else {
        setAssets([]);
      }
    } catch (error) {
      console.error("Error loading assets:", error);
      // Handle error state as needed
    } finally {
      setAssetsLoading(false);
    }
  };

  const handleChange = (e) => {
    const target = e.target.name;
    if (target === "assetTypes") {
      const value = e.value;
      setTeam((pre) => ({ ...pre, [target]: value, assets: [] }));
      loadAssets(value.map((x) => x.id));
    } else {
      const value = e.target.value;
      setTeam((pre) => ({ ...pre, [target]: value }));
    }
  };

  const onCheckboxChange = (e) => {
    setTeam((pre) => ({ ...pre, [e.target.name]: e.target.checked }));
  };

  const handlePropertyOptionChange = (event, selectedOption) => {
    const selectedValues = event
      .map((x) => x.value)
      .filter((x) => x !== "~all");
    if (
      selectedOption.action === "select-option" ||
      selectedOption.action === "remove-value"
    ) {
      if (
        selectedOption.option?.value !== "~all" &&
        selectedValues.length !== assets.length - 1
      ) {
        var assetss = assets.filter((x) => selectedValues.includes(x.value));
        setTeam((pre) => ({
          ...pre,
          assets: selectedValues,
          assetTypes: pre.assetTypes.map((x) => ({
            ...x,
            assets: assetss
              .filter((y) => y.assetType.id === x.id)
              .map((z) => z.value),
          })),
        }));
      } else {
        setTeam((pre) => ({
          ...pre,
          assets: ["~all"],
          assetTypes: pre.assetTypes.map((x) => ({
            ...x,
            assets: assets
              .filter((y) => y.value !== "~all" && y.assetType.id === x.id)
              .map((z) => z.value),
          })),
        }));
      }
    }
  };

  const loadData = async () => {
    const res = await axios.get(`${BASE_URL}/api/TeamMaster/${id}`);
    if (res.status === 200) {
      if (res.data.assetTypes && res.data.assetTypes.length > 0)
        loadAssets(res.data.assetTypes.map((x) => x.id));
      setTeam({
        ...res.data,
        // assets: res.data.assetIds.split(","),
        assetTypes: res.data.assetTypes.map((x) => ({
          ...x,
          assets: x.assetIds?.split(",") || [],
        })),
      });
    }
  };

  const loadAssetTypes = async () => {
    let res = await axios.get(`${BASE_URL}/Lookup/GetAllAssetTypes`);
    setAssetTypes(res.data);
  };

  const onSave = async () => {
    setLoading(true);
    const payload = {
      ...team,
      companyId: authCtx.profile.companyId,
      assetTypes: team.assetTypes.map((x) => ({
        ...x,
        assetIds: x.assets.join(","),
      })),
    };
    const res = await axios.post(`${BASE_URL}/api/TeamMaster`, payload);
    if (res.status === 200) {
      toast.success("Team Saved Successfully");
      navigate(`/teamForm/${res.data}`);
    } else {
      toast.error("Some Error Occured While Saving");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAssetTypes();
    if (id !== "new") loadData();
  }, []);

  return (
    <div className="flex flex-col relative text-white px-14 py-7 dark:bg-darkModeBg bg-lightModeBg h-full">
      <div className="flex text-xs font-medium gap-3 items-center">
        <button type="button" onClick={() => navigate("/teamList")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="1.5"
            stroke={darkMode ? "white" : "black"}
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19.5 12h-15m0 0l6.75 6.75M4.5 12l6.75-6.75"
            />
          </svg>
        </button>
        <label className="dark:text-white text-labels">
          {id === "new" ? t("button.addNew") : t("button.update")}
          &nbsp;{t("masters.headerTexts.teamMaster")}
        </label>
        <div className="flex h-10 w-10  dark:bg-zinc-750 bg-gray2 justify-center items-center rounded-full">
          <ReactSVG
            src="team-icon.svg"
            className="w-4 flex items-center justify-center"
            beforeInjection={(svg) => {
              svg.setAttribute(
                "style",
                `fill: ${darkMode ? "white" : "black"}`
              );
            }}
          />
        </div>
      </div>
      <div className="flex">
        <div className="flex flex-col text-xs font-medium py-4 gap-3 w-1/3">
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.name")}
            </label>
            <input
              type="text"
              name="name"
              placeholder="Name"
              onChange={handleChange}
              value={team.name}
              className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputPlaceHolderTxts px-5 w-full h-9"
            />
          </div>
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.desc")}
            </label>
            <input
              type="text"
              name="description"
              placeholder="Description"
              onChange={handleChange}
              value={team.description}
              className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light rounded-lg mb-4 dark:bg-zinc-750 bg-inputFields dark:text-white text-inputPlaceHolderTxts px-5 w-full h-9"
            />
          </div>
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.assetType")}
            </label>
            <div
              className={` w-full h-9 ${darkMode ? "darkCombo" : "lightCombo"}`}
            >
              <MultiSelect
                data={assetTypes}
                name="assetTypes"
                textField="name"
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingLeft: "12px",
                }}
                className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light dark:text-white text-inputFieldsTxts rounded-lg mb-4 w-full h-full"
                onChange={handleChange}
                value={team.assetTypes}
              />
              {/* <ComboBox
                data={assetTypes}
                name="assetTypes"
                textField="name"
                placeholder="Select Asset Type"
                suggest={true}
                clearButton={false}
                style={{
                  fontSize: "12px",
                  fontWeight: "500",
                  paddingLeft: "12px",
                }}
                className="block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts border-grey-light dark:text-white text-inputFieldsTxts rounded-lg mb-4 w-full h-full"
                onChange={handleChange}
                value={team.assetType}
              /> */}
            </div>
          </div>
          <div className="flex flex-col">
            <label className="dark:text-white text-labels text-sm font-medium py-2">
              {t("masters.fields.assets")}
            </label>
            <div
              className={` w-full h-9 ${
                darkMode ? "darkCreatableSelect" : "lightCreatableSelect"
              }`}
            >
              <CreatableSelect
                isMulti
                {...multiSelectStyle}
                options={assets}
                name="options"
                isLoading={assetsLoading}
                className="dark:text-white text-inputFieldsTxts dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts bg-zinc-700 rounded-xl w-full h-9"
                value={assets.filter((x) =>
                  team.assetTypes?.flatMap((x) => x.assets).includes(x.value)
                )}
                onChange={(e, o) => handlePropertyOptionChange(e, o)}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="flex justify-center items-center mt-4">
        <div className="flex justify-center form-check form-check-inline mr-2">
          <label
            className="form-check-label inline-block dark:placeholder:text-white placeholder:text-inputPlaceHolderTxts dark:text-white text-black mr-3 mt-1"
            forhtml="inlineCheckbox1"
          >
            {t("masters.fields.active")}
          </label>
          <input
            name="active"
            className="focus:ring-blue-500 h-4 lg:h-5 w-4 lg:w-5 text-blue-600 border-gray-300 rounded mt-1"
            type="checkbox"
            id="inlineCheckbox1"
            onChange={onCheckboxChange}
            checked={team.active}
            value={team.active}
          ></input>
        </div>
        <button
          type="button"
          name="Cancel"
          onClick={() => navigate("/teamList")}
          className="dark:bg-white bg-closeBtns dark:text-black text-closeBtnTxts  text-sm rounded-4xl w-48 h-9 justify-center mr-3"
        >
          {t("button.cancel")}
        </button>
        <button
          type="button"
          name="create"
          className="dark:bg-indigo-700 bg-submitBtns dark:text-white text-submitBtnTxts text-sm rounded-4xl w-48 h-9 disabled:opacity-40 justify-center"
          onClick={() => onSave()}
          disabled={loading}
        >
          {id === "new" ? t("button.create") : t("button.update")}
        </button>
      </div>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <ToastContainer />
    </div>
  );
};

export default TeamForm;
